/**
 * galaxy-jump-target.ts — v4M6.3a destination arrival-angle picker.
 *
 * Surfaces the continuous Jump Area mechanic (v4M5.3b3a / `HYPERSPACE.md
 * §4`) at the galaxy-view layer. The sim-side hooks already exist:
 *
 *  - `ShipInstance.jumpArrivalAngle: number | null` carries the picked
 *    angle through to `applyJumpShips`.
 *  - `Command.arrivalAngle` is on `moveAndJumpShips`.
 *  - `src/ui/input.ts` already derives the angle from cursor offset on
 *    galaxy-view RMB-on-system (centre dead-zone → null, off-centre →
 *    `Math.atan2(dy, dx)`).
 *
 * What this module adds is **discoverability** — without the visual
 * cue, a player who hasn't read the source has no idea the strategic
 * option exists. Per `docs/MAP.md §4.3.1`:
 *
 *  - Ring visibility is selection-driven: player has player-owned
 *    ships selected, sharing a single source system, AND the cursor
 *    hovers a destination system different from the source.
 *  - Empire-coloured ring on reachable destinations; muted grey on
 *    out-of-range (consistent with v4M5.3b3e's range-cap dimming).
 *  - Live cursor projection draws an inward-pointing marker on the
 *    ring at the projected angle. Centre dead-zone (< 25 screen px,
 *    matching the input handler's threshold) suppresses the marker —
 *    the player sees the ring but no chevron, telling them their
 *    click will use the "from source" default arrival.
 *
 * Ring radius in galaxy view is purely a visual abstraction — the sim
 * lands the ship at the destination's r=0.48 local-space ring. The
 * galaxy-view radius (`starRadius × 2.2`) is picked to sit in the
 * halo band but inside the per-system indicator column anchor
 * (`starRadius + 16` per MFix6.6).
 */

import type { Empire, GameState, ShipInstance, SystemId } from '../state/types/index';
import { hyperdriveRangeMultiplier } from '../game/research-effects';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';

/**
 * Cursor → ring projection. Mirrors the threshold used by the input
 * handler in `src/ui/input.ts` (~line 725): cursor within 25 screen
 * px of the star centre → centre dead-zone → null marker. Otherwise
 * the angle is `Math.atan2(dy, dx)` and the ring intercept sits at
 * radius `ringRadius` along that angle.
 */
export const CENTER_DEADZONE_PX = 25;

export interface RingProjection {
  /** Angle from star centre to cursor, in radians. `0` is screen-right (+x). */
  angle: number;
  /** Ring intercept x in screen px. */
  x: number;
  /** Ring intercept y in screen px. */
  y: number;
  /** True if the cursor sits inside the centre dead-zone. */
  isCenterDeadzone: boolean;
}

/**
 * Project a screen-space cursor onto a circular ring at radius
 * `ringRadius` around the star centre. Returns the angle, the ring
 * intercept, and a dead-zone flag so the caller can suppress the
 * arrival marker when the player's pick will fall back to the
 * default "from source" arrival.
 */
export function projectCursorToRing(
  starScreen: { x: number; y: number },
  ringRadius: number,
  cursorScreen: { x: number; y: number },
): RingProjection {
  const dx = cursorScreen.x - starScreen.x;
  const dy = cursorScreen.y - starScreen.y;
  const distance = Math.hypot(dx, dy);
  if (distance < CENTER_DEADZONE_PX) {
    return { angle: 0, x: starScreen.x, y: starScreen.y, isCenterDeadzone: true };
  }
  const angle = Math.atan2(dy, dx);
  return {
    angle,
    x: starScreen.x + Math.cos(angle) * ringRadius,
    y: starScreen.y + Math.sin(angle) * ringRadius,
    isCenterDeadzone: false,
  };
}

/**
 * Source-system + range derivation for the picker gate. Mirrors the
 * single-source check in `computeOutOfRangeSystemIds` (galaxy-view.ts)
 * — a fleet split across multiple source systems suppresses the
 * picker so the player isn't shown an ambiguous control.
 *
 * Returns `null` when:
 *  - no ships are selected
 *  - the viewer empire isn't found
 *  - any selected ship doesn't belong to the viewer empire
 *  - the selected ships span more than one source system
 *  - all selected ships are mid-jump (no source system to depart from)
 *
 * Otherwise returns the source `systemId` and the fleet's effective
 * hyperdrive range (min over selected ships of `template.hyperdriveRange
 * × hyperdriveRangeMultiplier`) so the caller can grey out unreachable
 * destinations.
 */
export interface PickerSource {
  systemId: SystemId;
  range: number;
  empire: Empire;
}

export function resolvePickerSource(state: GameState, ui: UiState): PickerSource | null {
  if (ui.selectedShipIds.size === 0) return null;
  const viewer = state.empires.get(viewerEmpireId(state, ui));
  if (!viewer) return null;
  let sourceSystemId: SystemId | null = null;
  let fleetRange = Number.POSITIVE_INFINITY;
  for (const id of ui.selectedShipIds) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    if (ship.ownerEmpireId !== viewer.id) return null;
    if (ship.systemId === null) continue; // mid-jump
    if (sourceSystemId === null) sourceSystemId = ship.systemId;
    else if (sourceSystemId !== ship.systemId) return null; // multi-source
    const tmpl = state.shipTemplates.get(`${viewer.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    const r = tmpl.hyperdriveRange * hyperdriveRangeMultiplier(state, viewer);
    if (r < fleetRange) fleetRange = r;
  }
  if (sourceSystemId === null) return null;
  if (!Number.isFinite(fleetRange)) return null;
  return { systemId: sourceSystemId, range: fleetRange, empire: viewer };
}

/**
 * Faint dashed Jump Area ring at the destination's screen-space
 * picker radius. Stroke width tracks zoom indirectly via the caller-
 * supplied `ringRadius`. Alpha low so the per-system indicator
 * column + ship sprites + path-line (v4M6.3b) layer cleanly on top.
 */
export function drawJumpTargetRing(
  ctx: CanvasRenderingContext2D,
  starScreen: { x: number; y: number },
  ringRadius: number,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 4]);
  ctx.globalAlpha = 0.7;
  ctx.beginPath();
  ctx.arc(starScreen.x, starScreen.y, ringRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * Inward-pointing arrival marker — a small chevron sitting on the
 * ring at the projected angle, pointing toward the star centre. The
 * "you arrive here" cue. Drawn solid (not dashed) and slightly
 * brighter than the ring so the eye picks it out.
 */
export function drawArrivalMarker(
  ctx: CanvasRenderingContext2D,
  starScreen: { x: number; y: number },
  proj: RingProjection,
  color: string,
): void {
  if (proj.isCenterDeadzone) return;
  // Unit vector along the inward direction (ring → star centre).
  const inward = {
    x: starScreen.x - proj.x,
    y: starScreen.y - proj.y,
  };
  const len = Math.hypot(inward.x, inward.y) || 1;
  const ux = inward.x / len;
  const uy = inward.y / len;
  // Perpendicular for the chevron's widthwise arms.
  const px = -uy;
  const py = ux;
  // Chevron tip sits a few px in front of the ring intercept (toward
  // the star), arms flare back to the ring tangent. Size in screen px.
  const TIP_LEAD = 6;
  const ARM_BACK = 8;
  const ARM_HALF = 7;
  const tipX = proj.x + ux * TIP_LEAD;
  const tipY = proj.y + uy * TIP_LEAD;
  const baseX = proj.x - ux * ARM_BACK;
  const baseY = proj.y - uy * ARM_BACK;
  const leftX = baseX + px * ARM_HALF;
  const leftY = baseY + py * ARM_HALF;
  const rightX = baseX - px * ARM_HALF;
  const rightY = baseY - py * ARM_HALF;
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(leftX, leftY);
  ctx.lineTo(rightX, rightY);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Top-level driver. Called once per frame from `galaxy-view.ts`. No-op
 * when the gate isn't satisfied (no selection, multi-source fleet,
 * cursor not over a candidate destination, etc.).
 *
 * `outOfRangeSystemIds` is the same set `galaxy-view.ts` computes for
 * the v4M5.3b3e range-cap dimming; passed through so the picker
 * surface uses the same authoritative range check as the per-star
 * desaturation pass.
 */
export function drawGalaxyJumpTargetPicker(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  starRadius: number,
  outOfRangeSystemIds: Set<SystemId>,
): void {
  if (ui.viewMode !== 'galaxy') return;
  if (ui.hoveredGalaxySystemId === null) return;
  if (ui.hoveredGalaxyAnchor === null) return;
  const source = resolvePickerSource(state, ui);
  if (source === null) return;
  // Picker is for arriving SOMEWHERE ELSE — suppress on the source.
  if (ui.hoveredGalaxySystemId === source.systemId) return;
  const destSys = state.systems.get(ui.hoveredGalaxySystemId);
  if (!destSys) return;

  const starScreen = worldToScreen(ui.galaxyCamera, vp, {
    x: destSys.x,
    y: destSys.y,
  });
  const ringRadius = starRadius * 2.2;
  const isReachable = !outOfRangeSystemIds.has(destSys.id);
  const ringColor = isReachable ? source.empire.color : 'rgba(150, 165, 195, 0.65)';

  drawJumpTargetRing(ctx, starScreen, ringRadius, ringColor);

  const proj = projectCursorToRing(starScreen, ringRadius, ui.hoveredGalaxyAnchor);
  // Marker only on reachable destinations — on out-of-range systems
  // the ring still shows (so the player understands the picker exists
  // there) but the chevron is suppressed because the click will be
  // rejected anyway.
  if (isReachable) {
    drawArrivalMarker(ctx, starScreen, proj, source.empire.color);
  }
}

// Internal re-export for tests + future callers that want to reach in
// without going through the driver.
export type { ShipInstance };
